import React, { useState } from 'react';
import {
  FileText,
  Edit3,
  Trash2,
  AlertCircle,
  DollarSign,
  Clock,
  Send
} from 'lucide-react';
import { NewPaymentMethod } from './NewPaymentMethodSelector';
import { computeVialMath, formatTotalMgPerVial } from '../../utils/vialMath';

interface PrescriptionData {
  productId: string;
  quantity: number;
  sig: string;
  pharmacyNotes: string;
  daysSupply: number;
}

interface ProductWithPrescription {
  product: {
    _id: string;
    drugName: string;
    concentration: string;
    format: string;
    size: string;
    displayName: string;
    basePrice: number;
    estimatedShipDays: number;
    calculatedPrice?: {
      finalPrice: number;
    };
  };
  prescription: PrescriptionData;
}

interface OrderReviewProps {
  patientName: string;
  prescriptions: ProductWithPrescription[];
  paymentMethod: NewPaymentMethod;
  onEditPrescription: (productId: string) => void;
  onRemovePrescription: (productId: string) => void;
  onWriteOrder: () => void;
  isWriting: boolean;
  markupOverrides?: Record<string, number>;
  onMarkupChange?: (productId: string, amount: number) => void;
}

export const OrderReview: React.FC<OrderReviewProps> = ({
  patientName,
  prescriptions,
  paymentMethod,
  onEditPrescription,
  onRemovePrescription,
  onWriteOrder,
  isWriting,
  markupOverrides,
  onMarkupChange
}) => {
  const [confirmingWrite, setConfirmingWrite] = useState(false);
  const isPatientPay = paymentMethod === 'patient_pay';

  const totalAmount = prescriptions.reduce((sum, item) => {
    const price = item.product.calculatedPrice?.finalPrice || item.product.basePrice;
    const markup = isPatientPay ? (markupOverrides?.[item.product._id] || 0) : 0;
    return sum + ((price + markup) * item.prescription.quantity);
  }, 0);

  const yourCostTotal = prescriptions.reduce((sum, item) => {
    const price = item.product.calculatedPrice?.finalPrice || item.product.basePrice;
    return sum + (price * item.prescription.quantity);
  }, 0);

  const handleWriteOrder = () => {
    if (confirmingWrite) {
      onWriteOrder();
    } else {
      setConfirmingWrite(true);
      setTimeout(() => setConfirmingWrite(false), 5000); // Reset after 5 seconds
    }
  };

  const getPaymentMethodDisplay = () => {
    return paymentMethod === 'patient_pay' 
      ? 'Patient Pays Online' 
      : 'Physician/Clinic Billed';
  };

  const getPaymentMethodDescription = () => {
    return paymentMethod === 'patient_pay'
      ? 'Patient pays securely online before prescription fulfillment'
      : 'Clinic pays pharmacy and may charge patient separately';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Order Review</h2>
            <p className="text-sm text-gray-600">
              Review all prescriptions for <span className="font-medium">{patientName}</span>
            </p>
          </div>
          <div className="text-right">
            {isPatientPay && totalAmount > yourCostTotal ? (
              <>
                <p className="text-sm text-gray-600">Patient Pays</p>
                <p className="text-2xl font-bold text-gray-900">${totalAmount.toFixed(2)}</p>
                <p className="text-xs text-gray-500">Your cost: ${yourCostTotal.toFixed(2)}</p>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600">Total Amount</p>
                <p className="text-2xl font-bold text-gray-900">${totalAmount.toFixed(2)}</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Payment Method Summary */}
      <div className="p-6 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900 mb-1">Payment Method</h3>
            <p className="text-sm text-gray-600">{getPaymentMethodDisplay()}</p>
            <p className="text-xs text-gray-500 mt-1">{getPaymentMethodDescription()}</p>
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <DollarSign className="h-4 w-4 mr-1" />
            {paymentMethod === 'patient_pay' ? 'Platform processed' : 'Markup may apply'}
          </div>
        </div>
      </div>

      {/* Prescriptions List */}
      <div className="p-6">
        <h3 className="font-medium text-gray-900 mb-4">
          Prescriptions ({prescriptions.length})
        </h3>
        
        {prescriptions.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No prescriptions added yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {prescriptions.map((item) => {
              const { product, prescription } = item;
              const price = product.calculatedPrice?.finalPrice || product.basePrice;
              const totalPrice = price * prescription.quantity;
              const markup = markupOverrides?.[product._id] || 0;
              const isOOS = (product as any).outOfStock === true;
              const isLowStock = (product as any).lowStockWarning === true;
              const showMarkup = isPatientPay && !!onMarkupChange;
              const isLab = (product as any).productType === 'lab';

              return (
                <div
                  key={product._id}
                  className={`rounded-lg border p-4 ${
                    isOOS
                      ? 'border-red-200 bg-red-50'
                      : isLowStock
                      ? 'border-amber-200 bg-amber-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {/* Product + pricing stack vertically on narrow screens, side by side once there's room */}
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                    {/* Product & prescription details */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-medium text-gray-900">
                          {product.displayName}
                        </h4>
                        {isOOS && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                            Out of Stock
                          </span>
                        )}
                        {isLowStock && !isOOS && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                            Low Stock
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-gray-500">
                        {isLab
                          ? `Lab panel${(product as any).lab?.panelCode ? ` • ${(product as any).lab.panelCode}` : ''}`
                          : `${product.drugName} • ${product.concentration} • ${product.format}`}
                      </p>

                      {(() => {
                        const total = formatTotalMgPerVial(computeVialMath(product));
                        return total ? (
                          <p className="text-xs text-gray-500">
                            <span className="font-medium text-gray-700">{total}</span>
                            <span className="text-gray-400"> (concentration × size)</span>
                          </p>
                        ) : null;
                      })()}

                      {isLowStock && !isOOS && (
                        <p className="text-xs text-amber-700 mt-1">
                          Low stock, please confirm availability for on time fulfillment
                        </p>
                      )}

                      {/* Qty / days / shipping — a lab panel has no days-supply and
                          isn't shipped by us, so it shows kit + turnaround instead. */}
                      {isLab ? (
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                          <span className="flex items-center text-gray-400">
                            <Clock className="h-3 w-3 mr-1" />
                            Results in ~7–10 days
                          </span>
                        </div>
                      ) : (
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                        <span>
                          Qty <span className="font-medium text-gray-900">{prescription.quantity}</span>
                        </span>
                        <span>
                          Days supply{' '}
                          <span className="font-medium text-gray-900">{prescription.daysSupply}</span>
                        </span>
                        <span className="flex items-center text-gray-400">
                          <Clock className="h-3 w-3 mr-1" />
                          {product.estimatedShipDays} days shipping
                        </span>
                      </div>
                      )}

                      {/* Sig for a medication; for a lab, the fulfillment expectation. */}
                      {isLab ? (
                        <div className="mt-3 rounded-md bg-violet-50 border border-violet-100 px-3 py-2">
                          <p className="text-sm text-gray-700 break-words">
                            An at-home collection kit is mailed to the patient. Fulfillment and
                            results typically take <span className="font-medium">7–10 days</span> from
                            order, depending on mailing and the lab.
                          </p>
                        </div>
                      ) : (
                      <div className="mt-3 rounded-md bg-gray-50 border border-gray-200 px-3 py-2">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">
                          Instructions (Sig)
                        </p>
                        <p className="text-sm text-gray-900 break-words">{prescription.sig}</p>
                        {prescription.pharmacyNotes && (
                          <p className="mt-2 text-xs text-gray-500 break-words">
                            <span className="font-medium">Pharmacy notes:</span>{' '}
                            {prescription.pharmacyNotes}
                          </p>
                        )}
                      </div>
                      )}
                    </div>

                    {/* Pricing */}
                    <div className="w-full shrink-0 rounded-md border border-gray-200 bg-gray-50 p-3 lg:w-64">
                      {showMarkup ? (
                        <div className="space-y-2">
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="text-xs text-gray-500">Your cost</span>
                            <span className="text-sm text-gray-700 text-right">
                              ${totalPrice.toFixed(2)}
                              <span className="block text-xs text-gray-400">
                                ${price.toFixed(2)} × {prescription.quantity}
                              </span>
                            </span>
                          </div>

                          <div className="flex items-center justify-between gap-3">
                            <span className="text-xs text-gray-500">Markup / unit</span>
                            <div className="flex items-center gap-1">
                              <span className="text-gray-400 text-sm">$</span>
                              <input
                                key={`markup-${product._id}-${markup}`}
                                type="number"
                                min="0"
                                step="0.01"
                                defaultValue={markup > 0 ? markup.toFixed(2) : ''}
                                onBlur={(e) => {
                                  const val = parseFloat(e.target.value);
                                  onMarkupChange(product._id, isNaN(val) || val < 0 ? 0 : val);
                                }}
                                placeholder="0.00"
                                className="w-20 px-2 py-1 text-right border border-gray-300 rounded text-sm focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          </div>

                          <div className="flex items-baseline justify-between gap-3 border-t border-gray-200 pt-2">
                            <span className="text-xs font-medium text-gray-600">Patient pays</span>
                            <span className="text-sm font-semibold text-gray-900 text-right">
                              ${((price + markup) * prescription.quantity).toFixed(2)}
                              <span className="block text-xs font-normal text-gray-400">
                                ${(price + markup).toFixed(2)} × {prescription.quantity}
                              </span>
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="text-xs text-gray-500">Price</span>
                          <span className="text-right">
                            <span className="text-lg font-semibold text-gray-900">
                              ${totalPrice.toFixed(2)}
                            </span>
                            <span className="block text-xs text-gray-500">
                              ${price.toFixed(2)} × {prescription.quantity}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-3 flex items-center justify-end gap-4 border-t border-gray-200 pt-3">
                    <button
                      onClick={() => onEditPrescription(product._id)}
                      className="text-sm font-medium text-blue-600 hover:text-blue-900 flex items-center"
                    >
                      <Edit3 className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => onRemovePrescription(product._id)}
                      className="text-sm font-medium text-red-600 hover:text-red-900 flex items-center"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="p-6 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {prescriptions.length} prescription{prescriptions.length !== 1 ? 's' : ''} ready to write
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="text-right">
              {isPatientPay && totalAmount > yourCostTotal ? (
                <>
                  <p className="text-sm text-gray-600">Patient Pays</p>
                  <p className="text-lg font-bold text-gray-900">${totalAmount.toFixed(2)}</p>
                  <p className="text-xs text-green-600">Your profit: +${(totalAmount - yourCostTotal).toFixed(2)}</p>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600">Total Amount</p>
                  <p className="text-lg font-bold text-gray-900">${totalAmount.toFixed(2)}</p>
                </>
              )}
            </div>
            
            <button
              onClick={handleWriteOrder}
              disabled={prescriptions.length === 0 || isWriting || prescriptions.some(p => (p.product as any).outOfStock === true)}
              className={`flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${
                confirmingWrite
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : prescriptions.length === 0 || isWriting || prescriptions.some(p => (p.product as any).outOfStock === true)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
              title={prescriptions.some(p => (p.product as any).outOfStock === true) ? 'Remove out-of-stock items before submitting' : undefined}
            >
              {isWriting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Writing Order...
                </>
              ) : confirmingWrite ? (
                <>
                  <AlertCircle className="h-5 w-5 mr-2" />
                  Click to Confirm
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Write Order
                </>
              )}
            </button>
          </div>
        </div>
        
        {confirmingWrite && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center text-red-800">
              <AlertCircle className="h-4 w-4 mr-2" />
              <p className="text-sm">
                Are you sure you want to write this order? This will submit {prescriptions.length} prescription{prescriptions.length !== 1 ? 's' : ''} to the pharmacy.
              </p>
            </div>
          </div>
        )}

        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-700">
            Expected fulfillment time is 2 business days. Not all orders are eligible for Saturday delivery.
          </p>
        </div>
      </div>
    </div>
  );
}; 